import {
  emptyInventorizationRegisterReading,
  type InventorizationMainReading,
  type InventorizationRegisterReading,
  readInventorizationRegisters,
  readInventorizationRemovedRegisterIds,
  validateInventorizationMainReading,
} from '@/features/inventorization/domain/inventorization-reading-selectors';
import { applyInventorizationReadingValidation } from '@/features/inventorization/domain/inventorization-reading-session';
import {
  createInventorizationRegisterId,
  createInventorizationRegisterLabel,
  DEFAULT_INVENTORIZATION_REGISTER_ID,
} from '@/features/inventorization/domain/inventorization-registers';
import type { CaptureSession } from '@/shared/capture/capture-session';

export function updateInventorizationMainReading(
  session: CaptureSession,
  patch: Partial<InventorizationMainReading>,
  updatedAt: string,
): CaptureSession {
  return updateInventorizationRegisterReading(
    session,
    readInventorizationRegisters(session)[0]?.id ?? DEFAULT_INVENTORIZATION_REGISTER_ID,
    patch,
    updatedAt,
  );
}

export function updateInventorizationRegisterReading(
  session: CaptureSession,
  registerId: string,
  patch: Partial<InventorizationRegisterReading>,
  updatedAt: string,
): CaptureSession {
  const existingRegisters = readInventorizationRegisters(session);
  const registerIndex = existingRegisters.findIndex(register => register.id === registerId);
  const targetIndex = registerIndex === -1 ? existingRegisters.length : registerIndex;
  const currentRegister = existingRegisters[registerIndex] ?? {
    ...emptyInventorizationRegisterReading,
    id: registerId,
    label: createInventorizationRegisterLabel(targetIndex),
    photoRef: registerId,
  };
  const registers = [...existingRegisters];
  registers[targetIndex] = {
    ...currentRegister,
    ...patch,
    id: registerId,
    photoRef: patch.photoRef ?? currentRegister.photoRef ?? registerId,
  };
  const inventorization = session.data.inventorization as object;
  const nextSession = {
    ...session,
    data: {
      ...session.data,
      inventorization: {
        ...inventorization,
        removedRegisterIds: readInventorizationRemovedRegisterIds(session).filter(id => id !== registerId),
        registers,
      },
    },
    updatedAt,
  };
  const issues = validateInventorizationMainReading(nextSession);

  return applyInventorizationReadingValidation(nextSession, issues);
}

export function removeInventorizationRegisterReading(
  session: CaptureSession,
  registerId: string,
  updatedAt: string,
): CaptureSession {
  const existingRegisters = readInventorizationRegisters(session);
  const removedRegister = existingRegisters.find(
    register => register.id === registerId || register.photoRef === registerId,
  );
  const registers = existingRegisters.filter(
    register => register.id !== registerId && register.photoRef !== registerId,
  );
  const inventorization = session.data.inventorization as object;
  const removedRegisterIds = [
    ...new Set(
      [...readInventorizationRemovedRegisterIds(session), registerId, removedRegister?.photoRef].filter(Boolean),
    ),
  ];
  const nextSession = {
    ...session,
    data: {
      ...session.data,
      inventorization: {
        ...inventorization,
        registers,
        removedRegisterIds,
      },
    },
    updatedAt,
  };
  const issues = validateInventorizationMainReading(nextSession);

  return applyInventorizationReadingValidation(nextSession, issues);
}

export function applyInventorizationRegisterDefaults(
  session: CaptureSession,
  registerDefaults: InventorizationRegisterDefault[],
  updatedAt: string,
) {
  return registerDefaults.reduce<CaptureSession>((currentSession, registerDefault, index) => {
    const registerId = createInventorizationRegisterId(index);

    return updateInventorizationRegisterReading(
      currentSession,
      registerId,
      {
        id: registerId,
        label: String(registerDefault.name ?? createInventorizationRegisterLabel(index)),
        obis: String(registerDefault.obis ?? ''),
        tariff: String(registerDefault.tariff ?? ''),
        unit: String(registerDefault.unit ?? ''),
      },
      updatedAt,
    );
  }, session);
}

export interface InventorizationRegisterDefault {
  name?: number | string;
  obis?: number | string;
  tariff?: number | string;
  unit?: number | string;
}
